import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import DataTable from "react-data-table-component";
import {
  TextField,
  Modal,
  Backdrop,
  Fade,
  Button,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  ListItem,
  List,
  Box,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Toast from "../../utils/Toast";

import parseCities from "../../utils/parseCities";

import Select from "react-select";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import useGetCities from "../../queries/city/getCities";
import Loader from "../Loader";

import Moment from "moment";

import { extendMoment } from "moment-range";

import { AuthContext } from "../../utils/userProvider";
import {
  modalSx,
  DeleteModalFrame,
  dataTableCustomStyles,
} from "../ListPage";

const moment = extendMoment(Moment);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "#ffffff",
    px: 0.75,
  },
};

const dateFieldSlotProps = (error, helperText, extra = {}) => ({
  textField: {
    variant: "outlined",
    fullWidth: true,
    margin: "normal",
    InputLabelProps: { shrink: true },
    sx: fieldSx,
    error: !!error,
    helperText: helperText || null,
    ...extra,
  },
});

const schema = yup.object().shape({
  city: yup.object().required("City is required"),
  startDate: yup.object().nullable().required("Start Date is required"),
  endDate: yup.object().nullable().required("End Date is required"),
  type: yup.string().nullable().required("Type is required"),
});

const CommissionTable = (props) => {
  const [tableData, setTableData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [cityOptions, setCityOptions] = useState([]);

  const { authState, setAuthState } = useContext(AuthContext);

  const [citiesData, resetCities] = useGetCities();

  const [openModal, setOpenModal] = useState(false);

  const [editRow, setEditRow] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [startDate, setStartDate] = useState(() => moment());
  const [endDate, setEndDate] = useState(() => moment());

  const [commissionType, setCommissionType] = useState(null);

  const [tierList, setTierList] = useState([]);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [defaultCommission, setDefaultCommission] = useState(null);

  function useOnMount(handler) {
    return React.useEffect(handler, []);
  }

  function required(displayName) {
    return function validateRequired(value) {
      return value !== null || `${displayName} is required.`;
    };
  }

  useOnMount(() => {
    register({ name: "startDate" }, { validate: required("Start Date") });
    register({ name: "endDate" }, { validate: required("End Date") });
    register({ name: "type" }, { validate: required("Type") });
  });

  const handleStartDate = (date) => {
    if (date) {
      setStartDate(date.startOf("day"));
      setValue("startDate", date.startOf("day"));
      if (endDate.isBefore(date.startOf("day"))) {
        setEndDate(date.endOf("day"));
        setValue("endDate", date.endOf("day"));
      }
    } else {
      setStartDate(null);
      setValue("startDate", null);
    }
  };

  const handleEndDate = (date) => {
    if (date) {
      setEndDate(date.endOf("day"));
      setValue("endDate", date.endOf("day"));
    } else {
      setEndDate(null);
      setValue("endDate", null);
    }
  };

  useEffect(() => {
    if (props.data) {
      setTableData(props.data);
    }
  }, [props.data]);

  useEffect(() => {
    if (citiesData.data) {
      let cities = citiesData.data;
      if (cities && cities.length > 0) {
        setCityOptions(parseCities(cities));
      }
    }
  }, [citiesData]);

  const cityChange = (value) => {
    if (value != null) {
      setValue("city", { label: value.label, value: value.value });
    }
  };

  const handleTypeChange = (event) => {
    setValue("type", event.target.value);
    setCommissionType(event.target.value);
  };

  const [mode, setMode] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    errors,
    getValues,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });

let columns = [
    {
      name: "City",
      sortable: false,
      cell: (row, index, column, id) => {
        let cityText = "";
        if (cityOptions && row.city) {
          let city = cityOptions.find((ele) => ele.value == row.city);
          if (city) {
            cityText = city.label;
          } else {
            cityText = "Deleted City";
          }
        }
        return <div>{cityOptions && <div>{cityText}</div>}</div>;
      },
    },
    {
      name: "Start Date",
      sortable: false,
      cell: (row, index, column, id) => {
        return <div>{moment(row.startDate).format("DD/MM/YYYY")}</div>;
      },
    },
    {
      name: "End Date",
      sortable: false,
      cell: (row, index, column, id) => {
        return <div>{moment(row.endDate).format("DD/MM/YYYY")}</div>;
      },
    },
    {
      name: "Type",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <div
            style={{
              textTransform: "capitalize",
            }}
          >
            {row.type}
          </div>
        );
      },
    },
    {
      name: "Options",
      cell: (row, index, column, id) => {
        if (authState.editAccess) {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CreateIcon />}
                onClick={() => {
                  editCommission(row, index);
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  deleteCommission(index);
                }}
              >
                Delete
              </Button>
            </div>
          );
        } else {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  editCommission(row, index);
                }}
              >
                View
              </Button>
            </div>
          );
        }
      },
    },
  ];

  useEffect(() => {
    if (errors) {
      console.log(errors);
    }
  }, [errors]);

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  /* const filteredItems = tableData.filter(
    (item) =>
      item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
  ); */

  var countDecimals = function (value) {
    if (Math.floor(value) === value) return 0;
    if (value.toString().split(".").length > 1) {
      return value.toString().split(".")[1].length || 0;
    } else {
      return 0;
    }
  };

  const newCommission = () => {
    setMode("new");
    setOpenModal(true);
    resetCommission();
    clearErrors("startDate");
    clearErrors("endDate");
  };

  const editCommission = (row, index) => {
    setMode("edit");

    setEditRow(row);
    setEditTarget(index);
    clearErrors("startDate");
    clearErrors("endDate");

    let defaultCommission = {};

    let cityOption = cityOptions.find((ele) => ele.value == row.city);
    if (cityOption) {
      console.log(cityOption);
      let defaultValue = { label: cityOption.label, value: cityOption.value };
      setValue("city", defaultValue);
      defaultCommission.city = defaultValue;
    }

    setValue("startDate", moment(row.startDate));
    setStartDate(moment(row.startDate));
    setValue("endDate", moment(row.endDate));
    setEndDate(moment(row.endDate));

    setCommissionType(row.type);
    setValue("type", row.type);

    console.log(row);

    if (row.type == "fixed") {
      setValue("fixedPercentage", Number(row.fixedPercentage));
      defaultCommission.fixedPercentage = Number(row.fixedPercentage);
    } else if (row.type == "tiered") {
      setValue("tieredBasePercentage", Number(row.tieredBasePercentage));
      defaultCommission.tieredBasePercentage = Number(row.tieredBasePercentage);

      if (row.tier) {
        setTierList(row.tier);
      }
    }

    setDefaultCommission(defaultCommission);

    setOpenModal(true);
  };

  const deleteCommission = (index) => {
    setDeleteTarget(index);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    let newTable = [...tableData];
    newTable.splice(deleteTarget, 1);
    props.setData(newTable);

    Toast.fire({
      icon: "success",
      title: "Commission removed",
      text: "Commission will be updated upon saving Agent.",
    });

    handleDeleteClose();
  };

  const handleDeleteClose = () => {
    setDeleteTarget(null);
    setDeleteModal(false);
  };

  const resetCommission = () => {
    setValue("startDate", moment());
    setStartDate(moment());
    setValue("endDate", moment());
    setEndDate(moment());

    setTierList([]);

    setDefaultCommission(null);

    setValue("type", null);
    setCommissionType(null);
  };

  const handleClose = () => {
    setOpenModal(false);
    setMode(null);
  };

  const onCommissionSubmit = (values) => {
    console.log(values);
    let pass = true;

    if (values.type == "fixed") {
      if (values.fixedPercentage == "0" || values.fixedPercentage == "") {
        pass = false;
        setError("fixedPercentage", {
          message: "Fixed Percentage is required",
        });
      } else if (Number.parseFloat(values.fixedPercentage) < 0) {
        pass = false;
        setError("fixedPercentage", {
          message: "Fixed Percentage cannot be negative",
        });
      } else {
        clearErrors("fixedPercentage");
      }
    } else if (values.type == "tiered") {
      if (
        values.tieredBasePercentage == "0" ||
        values.tieredBasePercentage == ""
      ) {
        pass = false;
        setError("tieredBasePercentage", {
          message: "Base Percentage is required",
        });
      } else if (Number.parseFloat(values.tieredBasePercentage) < 0) {
        pass = false;
        setError("tieredBasePercentage", {
          message: "Base Percentage cannot be negative",
        });
      } else {
        clearErrors("tieredBasePercentage");
      }
    }

    if (!pass) {
      return;
    }

    let inputDateRange = moment.range(
      moment(values.startDate).startOf("day"),
      moment(values.endDate).endOf("day")
    );
    //check clashing dates
    for (var a = 0; a < tableData.length; a++) {
      if (editTarget != a) {
        let range = moment.range(
          moment(tableData[a].startDate).startOf("day"),
          moment(tableData[a].endDate).endOf("day")
        );
        if (
          range.overlaps(inputDateRange) &&
          tableData[a].city == values.city.value
        ) {
          pass = false;
          setError("startDate", {
            message:
              "There is a conflict with other Commission Date Ranges of the same city, please select a different date.",
          });
          setError("endDate", {
            message:
              "There is a conflict with other Commission Date Ranges of the same city, please select a different date.",
          });
          break;
        }
      }
    }

    if (pass) {
      clearErrors("startDate");
      clearErrors("endDate");
    } else {
      return;
    }

    let tiers = [];

    //process tiered
    if (values.type == "tiered") {
      for (var i = 0; i < tierList.length; i++) {
        tiers.push({
          count: tierList[i].count,
          percentage: tierList[i].percentage,
        });
      }
    }

    //store data
    if (mode == "new") {
      let storedObject = {
        startDate: values.startDate.startOf("day").toDate(),
        endDate: values.endDate.endOf("day").toDate(),
        city: values.city.value,
        type: values.type,
      };
      if (values.type == "fixed") {
        storedObject.tier = [];
        storedObject.tieredBasePercentage = null;
        storedObject.fixedPercentage = Number.parseFloat(
          values.fixedPercentage
        );
      } else if (values.type == "tiered") {
        storedObject.fixedPercentage = null;
        storedObject.tieredBasePercentage = Number.parseFloat(
          values.tieredBasePercentage
        );
        storedObject.tier = tiers;
      }
      let newTable = [...tableData];
      newTable.push(storedObject);
      props.setData(newTable);

      Toast.fire({
        icon: "success",
        title: "Commission created",
        text: "Commission will be saved upon saving Agent.",
      });

      closeCommission();
    } else if (mode == "edit") {
      let newEditRow = { ...editRow };

      console.log(newEditRow);

      newEditRow.startDate = values.startDate.startOf("day").toDate();
      newEditRow.endDate = values.endDate.endOf("day").toDate();
      newEditRow.city = values.city.value;
      newEditRow.type = values.type;

      if (values.type == "fixed") {
        newEditRow.tier = [];
        newEditRow.tieredBasePercentage = null;
        newEditRow.fixedPercentage = Number.parseFloat(values.fixedPercentage);
      } else if (values.type == "tiered") {
        newEditRow.fixedPercentage = null;
        newEditRow.tieredBasePercentage = Number.parseFloat(
          values.tieredBasePercentage
        );
        newEditRow.tier = tiers;
      }

      let newTable = [...tableData];
      newTable[editTarget] = newEditRow;
      console.log(newTable);
      props.setData(newTable);

      Toast.fire({
        icon: "success",
        title: "Commission updated",
        text: "Commission will be updated upon saving Agent.",
      });

      closeCommission();
    }
  };

  const closeCommission = () => {
    setOpenModal(false);
    setMode(null);

    resetCommission();
  };

  const updateTextField = (event, parama) => {
    let value = event.target.value;
    if (countDecimals(value) > 2) {
      setValue(parama, Number(value).toFixed(2));
    } else {
      setValue(parama, Number(value));
    }
  };

  const updateCount = (event, index, target) => {
    let value = parseFloat(event.target.value);
    if (!value.toString().includes(".")) {
      let newTierList = [...tierList];
      newTierList[index][target] = Number(value).toFixed(0);
      setTierList(newTierList);
    }
  };

  const updateTierPercentage = (event, index, target) => {
    let value = parseFloat(event.target.value);
    if (countDecimals(value) > 2) {
      value = Number(value).toFixed(2);
    }
    let newTierList = [...tierList];
    newTierList[index][target] = value;
    setTierList(newTierList);
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="subHeader">
        {authState.editAccess && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={newCommission}
          >
            Create New
          </Button>
        )}
        <div className="subHeaderText">
          {props.selectedRows && props.selectedRows.length > 0 && (
            <h5 className="subHeaderTextContent">
              {props.selectedRows.length} Selected
            </h5>
          )}
        </div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText, props.selectedRows]);

  const values = getValues();

  const newTier = () => {
    let newTierList = [...tierList];
    newTierList.push({
      count: "",
      percentage: "",
    });
    setTierList(newTierList);
  };

  const deleteTier = (index) => {
    let newTierList = [...tierList];
    newTierList.splice(index, 1);
    setTierList(newTierList);
  };

  const renderContent = () => {
    return (
      <div>
        <DataTable
          columns={columns}
          data={tableData}
          customStyles={dataTableCustomStyles}
          pagination
          paginationPerPage={5}
          paginationRowsPerPageOptions={[5]}
          noHeader={true}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
        />
        <Modal
          aria-labelledby="edit-modal"
          aria-describedby="edit-modal-description"
          open={openModal}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            handleClose();
          }}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
          sx={modalSx}
        >
          <Fade in={openModal}>
            <Paper
              elevation={8}
              sx={{
                outline: "none",
                width: { xs: "92vw", sm: 640 },
                maxHeight: "90vh",
                overflow: "auto",
                borderRadius: 3,
                bgcolor: "#ffffff",
                backgroundColor: "#ffffff",
              }}
            >
              {mode && (
                <>
                  <Box
                    sx={{
                      bgcolor: "#00BAB2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 4,
                      py: 2,
                    }}
                  >
                    <Typography align="center" sx={{ color: "#fff", fontWeight: 700 }}>
                      {mode == "edit" && editRow && (
                        <span>
                          {authState.editAccess ? "Edit Commission" : "Commission"}
                        </span>
                      )}
                      {mode == "new" && <span>Create Commission</span>}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, bgcolor: "#ffffff", backgroundColor: "#ffffff" }}>
                      <form
                        noValidate
                        onSubmit={(event) => {
                          event.stopPropagation();
                          if (authState.editAccess) {
                            handleSubmit(onCommissionSubmit)(event);
                          }
                        }}
                      >
                        <div>
                          <p style={{ fontSize: 16, fontWeight: "bold" }}>City</p>
                          <Controller
                            name="city"
                            control={control}
                            defaultValue={
                              defaultCommission ? defaultCommission.city : null
                            }
                            render={({ ...props }) => {
                              return (
                                <Select
                                  styles={{
                                    control: (provided, state) => ({
                                      ...provided,
                                      borderColor: errors.city
                                        ? "red"
                                        : provided.borderColor,
                                    }),
                                    menu: (provided, state) => ({
                                      ...provided,
                                      zIndex: 100,
                                    }),
                                  }}
                                  options={cityOptions}
                                  onChange={cityChange}
                                  defaultValue={
                                    defaultCommission
                                      ? defaultCommission.city
                                      : null
                                  }
                                  isDisabled={!authState.editAccess}
                                  /* disabled={createCommissionResponse.loading || editCommissionResponse.loading} */
                                />
                              );
                            }}
                          />
                          {errors.city && (
                            <div style={{ marginLeft: 14, marginRight: 14, marginTop: 3, fontSize: "0.75rem", color: "#f44336" }}>
                              {errors.city.message}
                            </div>
                          )}
                        </div>

                        <DatePicker
                          label="Start Date"
                          value={startDate}
                          onChange={handleStartDate}
                          format="DD/MM/YYYY"
                          disabled={!authState.editAccess}
                          slotProps={dateFieldSlotProps(
                            errors.startDate,
                            errors.startDate && errors.startDate.message,
                            { id: "date-picker-start-date" }
                          )}
                        />
                        <DatePicker
                          label="End Date"
                          value={endDate}
                          onChange={handleEndDate}
                          format="DD/MM/YYYY"
                          minDate={startDate}
                          disabled={!authState.editAccess}
                          slotProps={dateFieldSlotProps(
                            errors.endDate,
                            errors.endDate && errors.endDate.message,
                            { id: "date-picker-end-date" }
                          )}
                        />
                        <div>
                          <p style={{ fontSize: 16, fontWeight: "bold" }}>Commission Type</p>
                        </div>
                        <FormControl component="fieldset">
                          <RadioGroup
                            aria-label="type"
                            name="type"
                            value={commissionType}
                            onChange={handleTypeChange}
                          >
                            <FormControlLabel
                              value="fixed"
                              control={
                                <Radio disabled={!authState.editAccess} />
                              }
                              label="Fixed"
                            />
                            <FormControlLabel
                              value="tiered"
                              control={
                                <Radio disabled={!authState.editAccess} />
                              }
                              label="Tiered"
                            />
                          </RadioGroup>
                          <FormHelperText>
                            {errors.type ? errors.type.message : ""}
                          </FormHelperText>
                        </FormControl>

                        {commissionType && commissionType == "fixed" && (
                          <div>
                            <div>
                              <p style={{ fontSize: 16, fontWeight: "bold", marginBottom: 0 }}>
                                Fixed Commission
                              </p>
                            </div>
                            <TextField
                              name="fixedPercentage"
                              margin="normal"
                              fullWidth
                              type="number"
                              label="Fixed Percentage (%)"
                              variant="outlined"
                              inputRef={register}
                              InputLabelProps={{ shrink: true }}
                              sx={fieldSx}
                              onChange={(e) => {
                                updateTextField(e, "fixedPercentage");
                              }}
                              defaultValue={
                                defaultCommission
                                  ? defaultCommission.fixedPercentage
                                  : null
                              }
                              error={errors.fixedPercentage != null}
                              helperText={
                                errors.fixedPercentage != null
                                  ? errors.fixedPercentage.message
                                  : null
                              }
                              disabled={!authState.editAccess}
                            />
                          </div>
                        )}

                        {commissionType && commissionType == "tiered" && (
                          <div>
                            <div>
                              <p style={{ fontSize: 16, fontWeight: "bold", marginBottom: 0 }}>
                                Tiered Commission
                              </p>
                            </div>
                            <TextField
                              name="tieredBasePercentage"
                              margin="normal"
                              fullWidth
                              type="number"
                              inputRef={register}
                              label="Tiered Base Percentage (%)"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              sx={fieldSx}
                              onChange={(e) => {
                                updateTextField(e, "tieredBasePercentage");
                              }}
                              error={errors.tieredBasePercentage != null}
                              helperText={
                                errors.tieredBasePercentage != null
                                  ? errors.tieredBasePercentage.message
                                  : null
                              }
                              defaultValue={
                                defaultCommission
                                  ? defaultCommission.tieredBasePercentage
                                  : null
                              }
                              disabled={!authState.editAccess}
                            />
                            <div style={{ fontSize: 16, fontWeight: "bold", marginBottom: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div>Tiers</div>
                              {authState.editAccess && (
                                <IconButton
                                  onClick={newTier}
                                  aria-label="add-commission"
                                >
                                  <AddIcon />
                                </IconButton>
                              )}
                            </div>
                            <List style={{ overflow: "auto", maxHeight: 180 }}>
                              {tierList.map((value, index) => {
                                const labelId = `tier-list-label-${index}`;
                                return (
                                  <ListItem style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}>
                                    <TextField
                                      margin="normal"
                                      fullWidth
                                      type="number"
                                      label="Reservation Count"
                                      variant="outlined"
                                      value={value.count}
                                      InputLabelProps={{ shrink: true }}
                                      sx={fieldSx}
                                      onChange={(e) => {
                                        updateCount(e, index, "count");
                                      }}
                                      disabled={!authState.editAccess}
                                    />
                                    <TextField
                                      margin="normal"
                                      fullWidth
                                      type="number"
                                      label="Percentage (%)"
                                      variant="outlined"
                                      inputRef={register}
                                      value={value.percentage}
                                      InputLabelProps={{ shrink: true }}
                                      sx={fieldSx}
                                      onChange={(e) => {
                                        updateTierPercentage(
                                          e,
                                          index,
                                          "percentage"
                                        );
                                      }}
                                      disabled={!authState.editAccess}
                                    />
                                    {authState.editAccess && (
                                      <IconButton
                                        onClick={() => {
                                          deleteTier(index);
                                        }}
                                        aria-label="deleteTier"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    )}
                                  </ListItem>
                                );
                              })}
                            </List>
                          </div>
                        )}
                        {authState.editAccess && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 30, gap: 8 }}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              /* disabled={editResponse.loading} */
                            >
                              Confirm
                            </Button>
                            <Button
                              onClick={handleClose}
                              variant="contained"
                              color="secondary"
                              /* disabled={editResponse.loading} */
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                        {!authState.editAccess && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 30, gap: 8 }}>
                            <Button
                              onClick={handleClose}
                              variant="contained"
                              color="secondary"
                              /* disabled={editResponse.loading} */
                            >
                              Close
                            </Button>
                          </div>
                        )}
                      </form>
                  </Box>
                </>
              )}
            </Paper>
          </Fade>
        </Modal>
        <Modal
          aria-labelledby="delete-modal"
          aria-describedby="delete-modal-description"
          open={deleteModal}
          onClose={handleDeleteClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
          sx={modalSx}
        >
          <Fade in={deleteModal}>
            <Box>
              <DeleteModalFrame title="Delete Commission">
                {deleteTarget != null && (
                  <div>
                    <p id="delete-modal-description">
                      Are you sure you want to delete the Commission?
                    </p>
                    <p>
                      This action cannot be{" "}
                      <b>
                        <u>reversed</u>
                      </b>
                      .
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                      <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="primary"
                      >
                        Confirm
                      </Button>
                      <Button
                        onClick={handleDeleteClose}
                        variant="contained"
                        color="secondary"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </DeleteModalFrame>
            </Box>
          </Fade>
        </Modal>
      </div>
    );
  };

  return (
    <div>
      {citiesData.loading && <Loader />}
      {!citiesData.loading && renderContent()}
    </div>
  );
};

export default CommissionTable;
